import * as yup from 'yup';
import type { TFunction } from 'i18next';
import type { AppointmentType } from '@/modules/appointment/api/appointmentType.api';
import {
  coerceAppointmentTypeColorToPreset,
  DEFAULT_APPOINTMENT_TYPE_COLOR,
  isAppointmentTypeColorPreset,
  normalizeAppointmentTypeColor,
} from './appointmentTypeColor';
import { APPOINTMENT_TYPE_DEFAULT_DURATIONS } from './appointmentTypes';
import {
  EMPTY_APPOINTMENT_TYPE_POLICY_FORM,
  mapOverrideToPolicyForm,
  mapPolicyFormToOverride,
  type OverlapInheritValue,
} from './appointmentTypePolicy';

/** Khớp maxlength trên model AppointmentType (backend). */
export const APPOINTMENT_TYPE_NAME_MAX_LENGTH = 150;

export interface AppointmentTypeFormValues {
  name: string;
  color: string;
  defaultDuration: string;
  isActive: boolean;
  dentistOverlap: OverlapInheritValue;
  chairOverlap: OverlapInheritValue;
}

export const emptyAppointmentTypeFormValues = (): AppointmentTypeFormValues => ({
  name: '',
  color: DEFAULT_APPOINTMENT_TYPE_COLOR,
  defaultDuration: '30',
  isActive: true,
  ...EMPTY_APPOINTMENT_TYPE_POLICY_FORM,
});

export const appointmentTypeToFormValues = (type: AppointmentType): AppointmentTypeFormValues => {
  const policy = mapOverrideToPolicyForm(type.bookingPolicyOverride);
  return {
    name: type.name,
    color: coerceAppointmentTypeColorToPreset(type.color),
    defaultDuration:
      typeof type.defaultDuration === 'number' ? String(type.defaultDuration) : '30',
    isActive: type.isActive,
    dentistOverlap: policy.dentistOverlap,
    chairOverlap: policy.chairOverlap,
  };
};

export const durationSelectOptions = (current: string): number[] => {
  const extra = Number(current);
  if (
    Number.isInteger(extra) &&
    extra > 0 &&
    !(APPOINTMENT_TYPE_DEFAULT_DURATIONS as readonly number[]).includes(extra)
  ) {
    return [...APPOINTMENT_TYPE_DEFAULT_DURATIONS, extra].sort((a, b) => a - b);
  }
  return [...APPOINTMENT_TYPE_DEFAULT_DURATIONS];
};

export const buildAppointmentTypeFormSchema = (t: TFunction<'appointment'>) =>
  yup.object({
    name: yup
      .string()
      .trim()
      .required(t('catalog.validation.nameRequired'))
      .max(APPOINTMENT_TYPE_NAME_MAX_LENGTH),
    color: yup
      .string()
      .required()
      .test('preset', t('catalog.validation.colorInvalid'), (value) =>
        isAppointmentTypeColorPreset(value || ''),
      ),
    defaultDuration: yup.string().required(),
    isActive: yup.boolean().required(),
    dentistOverlap: yup
      .mixed<OverlapInheritValue>()
      .oneOf(['inherit', 'block', 'warn', 'allow'])
      .required(),
    chairOverlap: yup
      .mixed<OverlapInheritValue>()
      .oneOf(['inherit', 'block', 'warn', 'allow'])
      .required(),
  });

export const formValuesToCreatePayload = (values: AppointmentTypeFormValues) => {
  const parsedDuration = Number(values.defaultDuration);
  const defaultDuration =
    Number.isInteger(parsedDuration) && parsedDuration > 0 ? parsedDuration : null;
  return {
    name: values.name.trim(),
    color: normalizeAppointmentTypeColor(values.color),
    defaultDuration,
    bookingPolicyOverride: mapPolicyFormToOverride({
      dentistOverlap: values.dentistOverlap,
      chairOverlap: values.chairOverlap,
    }),
  };
};

export const formValuesToUpdatePayload = (values: AppointmentTypeFormValues) => ({
  ...formValuesToCreatePayload(values),
  isActive: values.isActive,
});
